import cx from 'classnames';
import Image from 'next/image';

import {Accordion, Text} from '#component/base';
import {AccordionRoot} from '#component/util';
import {sprinkles} from '#style/sprinkles';

import {AccordionSectionProps} from './AccordionSection.types';

/**
 * This component renders a content section that includes an accordion of
 * various terms and associated definitions. This is used primarily by the
 * "Info" pages, including "Understanding your results" and
 * "Surprised by your results?".
 */
export const AccordionSection = ({
  className,
  title,
  body,
  items = [],
  ...otherProps
}: AccordionSectionProps) => (
  <div className={cx(sprinkles({layout: 'grid'}), className)} {...otherProps}>
    <Text
      as="h2"
      className={sprinkles({colStart: [1, 2, 2], colSpan: [4, 6, 4]})}
      color="deepBurgundy"
      fontWeight="light"
      variant="heading2">
      {title}
    </Text>

    <div
      className={sprinkles({
        colSpan: [4, 6, 5],
        colStart: [1, 2, 7],
        dir: 'col',
        flex: 'flex',
        gap: 32,
      })}>
      <Text as="p" color="mineShaft" variant="bodyLarge">
        {body}
      </Text>

      <AccordionRoot asChild collapsible type="single">
        <ul>
          {items.map(({header, title, copy}, i) => (
            <li key={i}>
              <Accordion trigger={title} value={`${i}`}>
                <Text as="div" className={sprinkles({flex: 'flex', dir: 'col', gap: 24})}>
                  {header && (
                    <Image
                      alt=""
                      aria-hidden
                      className={sprinkles({radius: 8})}
                      layout="responsive"
                      {...header}
                    />
                  )}
                  {copy}
                </Text>
              </Accordion>
            </li>
          ))}
        </ul>
      </AccordionRoot>
    </div>
  </div>
);
